import {AlertPolicy} from 'app/modules/Remote/Feature';
import {
  createLayerObjectFromLayerKeys,
  getPackageLayerKeys,
  parseLayerKey,
} from 'app/utils/layerUtils';

import {LayerDataset, LibraryDataset} from './LibraryTypes';

export type BulkMode = 'add' | 'remove';

// Reasons a (portfolio, layer) pair is excluded from the batch. Surfaced to the
// user on the review step so it's clear what we're handling for them.
export type SkipReason = 'already-enabled' | 'not-supported' | 'not-enrolled';

// One (portfolio, layer) pair the plan has a verdict on.
export interface BulkAction {
  layerKey: string;
  layerName: string;
  projectId: string;
}

export interface BulkSkip extends BulkAction {
  reason: SkipReason;
}

// One portfolio's worth of work: all selected layers that actually apply to it,
// merged into a single source-grouped layers object ready for the enrollment API.
export interface PortfolioPlan {
  projectId: string;
  featureCollectionId: number;
  // library keys acted on (post support/enrollment filtering, pre package-expansion)
  layerKeys: string[];
  // {sourceId: [layerId, ...]} — package keys expanded, grouped by source
  layersObject: Record<string, string[]>;
}

export interface BulkPlan {
  portfolioPlans: PortfolioPlan[];
  // the (portfolio × layer) enrollments that will be processed
  applied: BulkAction[];
  skipped: BulkSkip[];
}

/**
 * Given a mode, the selected library layer keys, and the selected portfolios,
 * work out which (portfolio, layer) pairs are actionable and which are skipped
 * (and why). Package layer keys are expanded and merged per portfolio so each
 * portfolio ends up with a single source-grouped `layersObject`.
 */
export function computeBulkPlan(
  mode: BulkMode,
  selectedLayerKeys: string[],
  selectedProjectIds: string[],
  layersByKey: Map<string, LibraryDataset<LayerDataset>>,
  projectIdToFcId: Record<string, number>
): BulkPlan {
  const applied: BulkAction[] = [];
  const skipped: BulkSkip[] = [];
  const keysByProject = new Map<string, string[]>();

  for (const projectId of selectedProjectIds) {
    const fcId = projectIdToFcId[projectId];
    if (fcId === undefined) {
      continue;
    }

    for (const layerKey of selectedLayerKeys) {
      const layer = layersByKey.get(layerKey);
      if (!layer) {
        continue;
      }

      const reason = pairSkipReason(mode, layer, fcId);
      if (reason) {
        skipped.push({layerKey, layerName: layer.name, projectId, reason});
        continue;
      }

      const existing = keysByProject.get(projectId) ?? [];
      existing.push(layerKey);
      keysByProject.set(projectId, existing);
      applied.push({layerKey, layerName: layer.name, projectId});
    }
  }

  const portfolioPlans: PortfolioPlan[] = [];
  for (const [projectId, layerKeys] of keysByProject.entries()) {
    const expandedKeys = layerKeys.flatMap((key) => getPackageLayerKeys(key));
    portfolioPlans.push({
      projectId,
      featureCollectionId: projectIdToFcId[projectId],
      layerKeys,
      layersObject: createLayerObjectFromLayerKeys(expandedKeys),
    });
  }

  return {portfolioPlans, applied, skipped};
}

/** Why one (portfolio, layer) pair is skipped, or undefined if it's actionable. */
function pairSkipReason(
  mode: BulkMode,
  layer: LibraryDataset<LayerDataset>,
  featureCollectionId: number
): SkipReason | undefined {
  const isEnrolled = layer.enrolledFeatureCollectionIds.includes(featureCollectionId);
  if (mode === 'remove') {
    return isEnrolled ? undefined : 'not-enrolled';
  }
  if (!layer.supportedFeatureCollectionIds.includes(featureCollectionId)) {
    return 'not-supported';
  }
  return isEnrolled ? 'already-enabled' : undefined;
}

/**
 * The one skip reason that holds for a layer across every chosen portfolio, or
 * undefined if at least one portfolio would actually change — so the picker can
 * rule a layer out instead of leaving it for the review step. `already-enabled`
 * wins over `not-supported` when both apply; it's the more useful thing to say.
 */
export function getLayerSkipReason(
  mode: BulkMode,
  layer: LibraryDataset<LayerDataset>,
  selectedFeatureCollectionIds: number[]
): SkipReason | undefined {
  const reasons: SkipReason[] = [];
  for (const featureCollectionId of selectedFeatureCollectionIds) {
    const reason = pairSkipReason(mode, layer, featureCollectionId);
    if (!reason) {
      return undefined;
    }
    reasons.push(reason);
  }
  return reasons.find((reason) => reason === 'already-enabled') ?? reasons[0];
}

/**
 * For bulk removal, find the alert policies that would be affected: policies on a
 * targeted feature collection whose (sourceId, layerId) matches a layer being
 * removed there. Mirrors the single-remove check in LensLibraryProvider.
 */
export function getAffectedAlertPolicies(
  portfolioPlans: PortfolioPlan[],
  alertPolicies: AlertPolicy[]
): AlertPolicy[] {
  if (!alertPolicies.length) {
    return [];
  }

  const removedLayerIdsByFcId = new Map<number, Set<string>>();
  for (const plan of portfolioPlans) {
    const layerIds = new Set<string>();
    for (const layerKey of plan.layerKeys.flatMap((key) => getPackageLayerKeys(key))) {
      const {sourceId, layerId} = parseLayerKey(layerKey);
      layerIds.add(`${sourceId}_${layerId}`);
    }
    removedLayerIdsByFcId.set(plan.featureCollectionId, layerIds);
  }

  return alertPolicies.filter((policy) => {
    const policyFcId =
      typeof policy.featureCollectionId === 'string'
        ? parseInt(policy.featureCollectionId)
        : policy.featureCollectionId;
    const removed = removedLayerIdsByFcId.get(policyFcId);
    return !!removed && removed.has(`${policy.sourceId}_${policy.layerId}`);
  });
}
